import './ReportForm.css';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../Common/LoadingIndicator';
import { createReportTicket } from '../../api/communication';
import { getCurrentUser } from '../../api/users_institutions';

// How long the success state stays visible before returning to the default reports view --
// mirrors the verification-success timing already used in Login.jsx/Signup.jsx.
const SUCCESS_AUTOCLOSE_MS = 1800;

// Mirrors ReportTicketSerializer.DESCRIPTION_MAX_WORDS (api/serializers.py) -- this is only a
// live hint for the user, the backend is still the actual source of truth/enforcement.
const DESCRIPTION_MAX_WORDS = 400;

const countWords = (text) => {
    const trimmed = text.trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
};

// Embedded directly, always mounted, in Settings.jsx's "reports" sd-section -- no .Modal/
// .modal-box wrapper like its predecessor, Modal/ModalReport.jsx, and no parent-controlled
// open/closed state: it resets itself back to a blank form a moment after a successful submit
// instead of calling back out to a parent to be unmounted.
function ReportForm() {
    const { t, i18n } = useTranslation();
    const currentUser = getCurrentUser();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [descriptionWordCount, setDescriptionWordCount] = useState(0);
    // Withholds the reporter's name from what staff see in ManageReports.jsx (see
    // ReportTicket.first_name/last_name in models.py) -- `user` still ties the ticket to this
    // account regardless, so this isn't anonymous to the backend, just to the report manager.
    const [isAnonymous, setIsAnonymous] = useState(false);

    // The fields below are unmounted entirely while `submitted` is true (swapped out by the
    // ternary in the JSX), so flipping it back to false alone is enough to reset them -- React
    // remounts fresh <input>/<textarea> elements with their original defaultValue/empty state,
    // no manual DOM reset needed (and none would be safe here anyway, since this runs before
    // that remount happens).
    const resetForm = () => {
        setSubmitted(false);
        setIsAnonymous(false);
        setDescriptionWordCount(0);
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setErrorMsg('');

        const first_name = isAnonymous ? '' : document.getElementById('reportFirstName').value.trim();
        const last_name = isAnonymous ? '' : document.getElementById('reportLastName').value.trim();
        const report_type = document.getElementById('reportType').value;
        const description = document.getElementById('reportDescription').value.trim();

        if ((!isAnonymous && (!first_name || !last_name)) || !report_type || !description) {
            setErrorMsg(t('modalReport.allFieldsRequired'));
            return;
        }

        setIsSubmitting(true);
        try {
            await createReportTicket({ first_name, last_name, report_type, description, lang: i18n.language });
            setSubmitted(true);
            setTimeout(resetForm, SUCCESS_AUTOCLOSE_MS);
        } catch (error) {
            console.error('Error submitting report ticket:', error);
            setErrorMsg(t('modalReport.submitFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="rf-wrapper">
            <LoadingIndicator active={isSubmitting} label={t('common.loading')} />

            {submitted ? (
                <div className="rf-success">
                    <div className="rf-icon-pop">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6L9 17l-5-5"></path>
                        </svg>
                    </div>
                    <h2>{t('modalReport.successTitle')}</h2>
                    <p>{t('modalReport.successBody')}</p>
                </div>
            ) : (
                <>
                    {errorMsg && <div className="rf-error-msg">{errorMsg}</div>}

                    {/* Kept mounted (just hidden) rather than unmounted while anonymous, so
                        whatever the user already typed survives toggling back off. */}
                    <div className="rf-field-row" style={{ display: isAnonymous ? 'none' : undefined }}>
                        <div>
                            <label className="label-text">{t('modalReport.firstNameLabel')}</label>
                            <input
                                type="text"
                                id="reportFirstName"
                                className="login-input"
                                defaultValue={currentUser?.first_name || ''}
                                placeholder={t('modalReport.firstNamePlaceholder')}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div>
                            <label className="label-text">{t('modalReport.lastNameLabel')}</label>
                            <input
                                type="text"
                                id="reportLastName"
                                className="login-input"
                                defaultValue={currentUser?.last_name || ''}
                                placeholder={t('modalReport.lastNamePlaceholder')}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <label className="label-text">{t('modalReport.reportTypeLabel')}</label>
                    <select id="reportType" className="login-input" defaultValue="bug" disabled={isSubmitting} style={{ marginBottom: '20px', cursor: 'pointer' }}>
                        <option value="bug">{t('modalReport.reportTypeBug')}</option>
                        <option value="content">{t('modalReport.reportTypeContent')}</option>
                        <option value="account">{t('modalReport.reportTypeAccount')}</option>
                        <option value="privacy">{t('modalReport.reportTypePrivacy')}</option>
                        <option value="feature">{t('modalReport.reportTypeFeature')}</option>
                        <option value="content_request">{t('modalReport.reportTypeContentRequest')}</option>
                        <option value="other">{t('modalReport.reportTypeOther')}</option>
                    </select>

                    <label className="label-text">{t('modalReport.descriptionLabel')}</label>
                    <textarea
                        id="reportDescription"
                        className="login-input"
                        placeholder={t('modalReport.descriptionPlaceholder')}
                        disabled={isSubmitting}
                        onChange={(e) => setDescriptionWordCount(countWords(e.target.value))}
                        style={{ minHeight: '100px', resize: 'vertical' }}
                    ></textarea>
                    <p className={`rf-word-count${descriptionWordCount > DESCRIPTION_MAX_WORDS ? ' rf-word-count-over' : ''}`}>
                        {t('modalReport.wordCount', { count: descriptionWordCount, max: DESCRIPTION_MAX_WORDS })}
                    </p>

                    <div className="rf-actions">
                        <button className="btn-primary sd-btn-inline" onClick={handleSubmit} disabled={isSubmitting}>
                            {t('modalReport.submit')}
                        </button>
                        <button
                            type="button"
                            className={`btn-primary sd-btn-inline rf-anonymous-btn ${isAnonymous ? 'active' : ''}`}
                            style={{ background: 'var(--bg-secondary)', color: 'var(--text-main)', border: '1px solid var(--border)', boxShadow: 'none' }}
                            onClick={() => setIsAnonymous((prev) => !prev)}
                            disabled={isSubmitting}
                        >
                            {t('modalReport.anonymous')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default ReportForm;
